import React from "react";
import { cva } from "class-variance-authority";

import {
  type BaseField,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RadioGroup,
  RadioGroupItem,
  withConditional,
} from "@bleu.builders/ui";
import { cn } from "@perk/core/lib/utils";
import type { FieldValues, UseFormReturn } from "react-hook-form";

export interface RadioGroupFieldProps extends BaseField {
  options: Array<{
    label: string;
    tooltip?: string;
    value: string;
  }>;
}

const radioGroupVariants = cva("w-full flex flex-col gap-2", {
  variants: {
    layout: {
      stack: "items-stretch",
      inline: "items-stretch",
    },
  },
  defaultVariants: {
    layout: "stack",
  },
});

interface RadioGroupWithoutSectionProps {
  field: RadioGroupFieldProps;
  form: UseFormReturn<FieldValues>;
}

const RadioGroupWithoutSection = ({
  form,
  field,
}: RadioGroupWithoutSectionProps) => (
  <FormField
    control={form.control}
    name={field.name}
    rules={field.required ? { required: true } : {}}
    render={({ field: formField }) => (
      <FormItem className="space-y-4 w-full">
        <div className="space-y-2">
          <FormLabel className="text-lg font-semibold" tooltip={field.tooltip}>
            {field.label}
          </FormLabel>
          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
        </div>

        <FormControl>
          <RadioGroup
            onValueChange={formField.onChange}
            value={formField.value}
            className={cn(radioGroupVariants())}
          >
            {field?.options?.map((option) => (
              <FormItem key={option.value} className="w-full">
                <label className="cursor-pointer block" htmlFor={option.value}>
                  <div
                    className={cn(
                      "w-full p-4 rounded-xl flex items-center gap-3 transition-colors",
                      formField.value === option.value
                        ? "bg-gray-200 hover:bg-gray-300"
                        : "bg-gray-100 hover:bg-gray-200"
                    )}
                  >
                    <FormControl>
                      <RadioGroupItem
                        id={option.value}
                        value={option.value}
                        className="data-[state=checked]:border-black data-[state=checked]:border-2"
                      />
                    </FormControl>
                    <span className="font-medium">{option.label}</span>
                  </div>
                </label>
              </FormItem>
            ))}
          </RadioGroup>
        </FormControl>

        {field.required && (
          <p className="text-sm text-gray-500">You must choose one answer</p>
        )}
        <FormMessage />
      </FormItem>
    )}
  />
);

export const RadioGroupField = withConditional<RadioGroupFieldProps>(
  ({ form, field }) => {
    return <RadioGroupWithoutSection {...{ form, field }} />;
  }
);
